import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDigitalReceptionMetadata,
  useAccessSettings,
} from "./useQueryBoard";
import { useAccessControlAutoSwitch } from "./useAccessControlAutoSwitch";

interface UseAccessNavigationParams {
  type?: string;
  id?: string;
}

export const useCheckinAccessNavigation = ({
  type,
  id,
}: UseAccessNavigationParams = {}) => {
  const navigate = useNavigate();

  const { enableExternalDoorTrigger, accessControl } =
    useDigitalReceptionMetadata();

  const {
    isAccessAllowedForGuestsForCheckin,
    isAccessAllowedForMeetingAttendeesForCheckin,
    isAccessEnabledForAutoOpenForCheckin,
    accessController,
  } = useAccessSettings();

  const { trigger, doorStatus, doorModal } = useAccessControlAutoSwitch();

  const willNavigateToCheckinAccessControl = useMemo(() => {
    return !!(
      accessController &&
      ((type === "MeetingAttendee" &&
        isAccessAllowedForMeetingAttendeesForCheckin) ||
        (type === "Guest" && isAccessAllowedForGuestsForCheckin))
    );
  }, [
    type,
    accessController,
    isAccessAllowedForMeetingAttendeesForCheckin,
    isAccessAllowedForGuestsForCheckin,
  ]);

  const willNavigateToCheckinAccessControlPage = useMemo(() => {
    return (
      willNavigateToCheckinAccessControl &&
      !(isAccessEnabledForAutoOpenForCheckin && accessController)
    );
  }, [
    willNavigateToCheckinAccessControl,
    isAccessEnabledForAutoOpenForCheckin,
    accessController,
  ]);

  const handleCheckinAccessNavigation = useCallback(() => {
    if (
      enableExternalDoorTrigger &&
      (type === "MeetingAttendee" || type === "Guest")
    ) {
      navigate("/door", { replace: true });
      return;
    }

    if (accessControl && type === "MeetingAttendee") {
      navigate(`/door-control/${id}`, { replace: true });
      return;
    }

    if (willNavigateToCheckinAccessControl) {
      if (isAccessEnabledForAutoOpenForCheckin && accessController) {
        trigger(accessController);
        return;
      }
      navigate(`/access-control/${accessController}`, { replace: true });
      return;
    }
    navigate("/", { replace: true });
  }, [
    id,
    type,
    accessControl,
    enableExternalDoorTrigger,
    accessController,
    willNavigateToCheckinAccessControl,
    isAccessEnabledForAutoOpenForCheckin,
    trigger,
    navigate,
  ]);

  return {
    handleCheckinAccessNavigation,
    willNavigateToCheckinAccessControl,
    willNavigateToCheckinAccessControlPage,
    isDoorOpening: !!doorStatus,
    accessSwitchModal: doorModal,
  };
};
